import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { ProTeaserSection } from "@/components/pro-gate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HardHat, Plus, Pencil, Trash2, Loader2, Users, Save, CheckCircle2 } from "lucide-react";
import { Redirect } from "wouter";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type FieldWorker = {
  id: number;
  name: string;
  email: string;
  hourlyRate: string | null;
  createdAt: string;
};

const TEAM_QUERY_KEY = ["team"];

async function fetchTeam(): Promise<FieldWorker[]> {
  const res = await fetch(`${BASE}/api/team`);
  if (!res.ok) throw new Error("Failed to fetch team");
  return res.json();
}

export default function TeamSettingsPage() {
  usePageTitle("Team");
  const { user } = useAuth();
  const { hasProAccess } = getTrialInfo(user);

  // Only owners can access this page
  if (user && user.role !== "owner") {
    return <Redirect to="/field" />;
  }

  if (!hasProAccess) {
    return (
      <div>
        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Users style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Team
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
            Invite field workers and manage their access.
          </p>
        </div>
        <ProTeaserSection
          title="Team Management"
          description="Invite field workers, set hourly rates, and give them access to log time and capture photos on-site."
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {[
              { name: "Jake Miller", email: "jake@example.com", rate: "$45/hr" },
              { name: "Sarah Chen", email: "sarah@example.com", rate: "$52/hr" },
              { name: "Marcus Webb", email: "marcus@example.com", rate: "$38/hr" },
            ].map((w) => (
              <Card key={w.name}>
                <CardContent style={{ padding: "0.875rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{w.name}</div>
                    <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{w.email}</div>
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>{w.rate}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ProTeaserSection>
      </div>
    );
  }

  return <TeamSettingsContent />;
}

function TeamSettingsContent() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const queryClient = useQueryClient();

  const teamQuery = useQuery({ queryKey: TEAM_QUERY_KEY, queryFn: fetchTeam });
  const workers = teamQuery.data ?? [];

  // Owner's own labor rate
  const [ownerRate, setOwnerRate] = useState(user?.hourlyRate != null ? String(user.hourlyRate) : "");
  const [ownerRateLoading, setOwnerRateLoading] = useState(false);
  const [ownerRateSaved, setOwnerRateSaved] = useState(false);

  useEffect(() => {
    if (user?.hourlyRate != null) setOwnerRate(String(user.hourlyRate));
  }, [user?.hourlyRate]);

  const handleSaveOwnerRate = async () => {
    setOwnerRateLoading(true);
    setOwnerRateSaved(false);
    try {
      const res = await fetch(`${BASE}/api/auth/profile`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: user?.name ?? "",
          hourlyRate: ownerRate.trim() ? parseFloat(ownerRate) : null,
        }),
      });
      if (!res.ok) throw new Error();
      setOwnerRateSaved(true);
      setTimeout(() => setOwnerRateSaved(false), 3000);
    } catch {
      toast({ title: "Failed to save rate", variant: "destructive" });
    } finally {
      setOwnerRateLoading(false);
    }
  };

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRate, setInviteRate] = useState("");

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/team/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: inviteName.trim(),
          email: inviteEmail.trim(),
          hourlyRate: inviteRate ? parseFloat(inviteRate) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to invite");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Field worker invited", description: `An invite email was sent to ${inviteEmail.trim()}` });
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
      setInviteOpen(false);
      setInviteName("");
      setInviteEmail("");
      setInviteRate("");
    },
    onError: (err: Error) => {
      toast({ title: "Failed to invite", description: err.message, variant: "destructive" });
    },
  });

  // Edit dialog
  const [editWorker, setEditWorker] = useState<FieldWorker | null>(null);
  const [editName, setEditName] = useState("");
  const [editRate, setEditRate] = useState("");

  const openEdit = (w: FieldWorker) => {
    setEditWorker(w);
    setEditName(w.name);
    setEditRate(w.hourlyRate ?? "");
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editWorker) return;
      const res = await fetch(`${BASE}/api/team/${editWorker.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim() || undefined, hourlyRate: editRate ? parseFloat(editRate) : null }),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Worker updated" });
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
      setEditWorker(null);
    },
    onError: () => toast({ title: "Failed to update", variant: "destructive" }),
  });

  const removeMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/team/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      toast({ title: "Field worker removed" });
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY });
    },
    onError: () => toast({ title: "Failed to remove", variant: "destructive" }),
  });

  return (
    <div style={{ maxWidth: "48rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.75rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
            <HardHat style={{ width: "1.375rem", height: "1.375rem", color: "hsl(var(--primary))" }} />
            Field Team
          </h1>
          <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
            Manage field workers who can capture photos and log time on your projects.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
          Invite Field Worker
        </Button>
      </div>

      {/* Owner's own labor rate */}
      <div style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", padding: "1.125rem 1.25rem", marginBottom: "1.75rem" }}>
        <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>Your labor rate</div>
        <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.875rem" }}>
          Applied to your own time log entries on projects.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={ownerRate}
              onChange={(e) => { setOwnerRate(e.target.value); setOwnerRateSaved(false); }}
              placeholder="0.00"
              disabled={ownerRateLoading}
              style={{ paddingLeft: "1.75rem", width: "130px" }}
            />
          </div>
          <span style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>/hr</span>
          <Button size="sm" onClick={handleSaveOwnerRate} disabled={ownerRateLoading}>
            {ownerRateLoading
              ? <Loader2 style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} className="animate-spin" />
              : <Save style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} />}
            Save
          </Button>
          {ownerRateSaved && (
            <span style={{ fontSize: "0.8125rem", color: "hsl(var(--success, 142 76% 36%))", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <CheckCircle2 style={{ width: "0.875rem", height: "0.875rem" }} /> Saved
            </span>
          )}
        </div>
      </div>

      {/* Worker list */}
      {teamQuery.isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <Loader2 style={{ width: "1.5rem", height: "1.5rem" }} className="animate-spin text-muted-foreground" />
        </div>
      ) : workers.length === 0 ? (
        <div style={{ border: "1px dashed hsl(var(--border))", borderRadius: "0.75rem", padding: "3rem 2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
          <Users style={{ width: "2.5rem", height: "2.5rem", margin: "0 auto 0.75rem", opacity: 0.3 }} />
          <p style={{ fontWeight: 500, marginBottom: "0.25rem" }}>No field workers yet</p>
          <p style={{ fontSize: "0.875rem" }}>Invite a field worker to give them access to photos and time tracking.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {workers.map((w) => (
            <div
              key={w.id}
              style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>{w.name}</div>
                <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{w.email}</div>
              </div>
              {w.hourlyRate && (
                <div style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                  <span style={{ fontWeight: 600, color: "hsl(var(--foreground))" }}>{formatCurrency(parseFloat(w.hourlyRate))}</span>/hr
                </div>
              )}
              <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                <Button variant="outline" size="sm" onClick={() => openEdit(w)}>
                  <Pencil style={{ width: "0.75rem", height: "0.75rem", marginRight: "0.25rem" }} />
                  Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" style={{ color: "hsl(var(--destructive))", borderColor: "hsl(var(--destructive) / 0.3)" }}>
                      <Trash2 style={{ width: "0.75rem", height: "0.75rem" }} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {w.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove their account and access. Their time entries and photos will remain attached to projects.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => removeMutation.mutate(w.id)}
                        style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
                      >
                        {removeMutation.isPending ? <Loader2 style={{ width: "0.875rem", height: "0.875rem" }} className="animate-spin mr-1" /> : null}
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { if (!open) { setInviteOpen(false); setInviteName(""); setInviteEmail(""); setInviteRate(""); } }}>
        <DialogContent style={{ maxWidth: "26rem" }}>
          <DialogHeader>
            <DialogTitle>Invite Field Worker</DialogTitle>
            <DialogDescription>
              They'll receive an email with a link to set their password and access the Field section.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Name</Label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Jane Smith"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="jane@example.com"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Hourly Rate (optional)</Label>
              <div style={{ position: "relative", marginTop: "0.375rem" }}>
                <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={inviteRate}
                  onChange={(e) => setInviteRate(e.target.value)}
                  placeholder="0.00"
                  style={{ paddingLeft: "1.75rem" }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending || !inviteName.trim() || !inviteEmail.trim()}
            >
              {inviteMutation.isPending ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" /> : <Plus style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editWorker} onOpenChange={(open) => { if (!open) setEditWorker(null); }}>
        <DialogContent style={{ maxWidth: "26rem" }}>
          <DialogHeader>
            <DialogTitle>Edit Field Worker</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Name</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ marginTop: "0.375rem" }}
              />
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Hourly Rate</Label>
              <div style={{ position: "relative", marginTop: "0.375rem" }}>
                <span style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  placeholder="0.00"
                  style={{ paddingLeft: "1.75rem" }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditWorker(null)}>Cancel</Button>
            <Button onClick={() => editMutation.mutate()} disabled={editMutation.isPending || !editName.trim()}>
              {editMutation.isPending ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
